import arrowDownIcon from "@loot-os/icons/icons/arrow-down";
import arrowUpIcon from "@loot-os/icons/icons/arrow-up";
import sortIcon from "@loot-os/icons/icons/sort";
import { type ReactNode, useId } from "react";

import { cn, tv } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { Icon } from "./Icon.js";
import { Skeleton } from "./Skeleton.js";

export type SortDirection = "asc" | "desc";

export interface TableSort {
  key: string;
  direction: SortDirection;
}

export interface TableColumn<Row> {
  /** Unique within the table; what `sort.key` names. */
  key: string;
  /** The column's name. Always words, even when it is visually an icon. */
  header: ReactNode;
  /** The cell for one row. */
  cell: (row: Row) => ReactNode;
  /** A number to compare down the column: mono, tabular, end-aligned. */
  numeric?: boolean;
  /** End-aligns a column that isn't numeric (a status, an action). */
  align?: "start" | "end";
  /** Its header sorts the rows. The sorting itself is the caller's, through `onSortChange`. */
  sortable?: boolean;
  /**
   * The cell that names its row (a date, an item's name), read before each cell of that row. One
   * column per table; the first column when none says so.
   */
  rowHeader?: boolean;
  /** Hides the column below a width. What it holds must be reachable another way there. */
  hideBelow?: "sm" | "md" | "lg";
  /** Never wraps: an amount, a date, a short status. */
  nowrap?: boolean;
  /** A fixed width, e.g. "8rem"; the rest share what is left. */
  width?: string;
}

export interface TableProps<Row> {
  /** What the table lists: "Balance history". It names the table; `hideCaption` keeps it for screen readers only. */
  caption: string;
  hideCaption?: boolean;
  columns: readonly TableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
  /** Draws placeholder rows (and keeps the rows given) while the first page loads. */
  loading?: boolean;
  /** How many placeholder rows; match the page size so nothing jumps. */
  loadingRows?: number;
  /** Shown in place of rows when there are none: an EmptyState, or a line of text. */
  empty?: ReactNode;
  /** Under the table, inside its frame: a LoadMore. */
  footer?: ReactNode;
  /** `compact` for dense staff tables; `comfortable` for a shopper's own history. */
  density?: "comfortable" | "compact";
  className?: string;
}

const HIDE: Record<NonNullable<TableColumn<unknown>["hideBelow"]>, string> = {
  sm: "max-sm:hidden",
  md: "max-md:hidden",
  lg: "max-lg:hidden",
};

const cellStyles = tv({
  base: "px-4 text-start align-middle",
  variants: {
    density: { comfortable: "py-3.5", compact: "py-2" },
    end: { true: "text-end" },
    numeric: { true: "font-mono text-mono-meta tabular-nums" },
    nowrap: { true: "whitespace-nowrap" },
  },
});

/** Keys for placeholder rows, which have no identity of their own. */
function placeholderKeys(count: number): string[] {
  return Array.from({ length: count }, (_, i) => `placeholder-${i}`);
}

function SortIcon({ direction }: { direction: SortDirection | undefined }) {
  return (
    <Icon
      icon={direction === "asc" ? arrowUpIcon : direction === "desc" ? arrowDownIcon : sortIcon}
      size={14}
      className={cn("shrink-0", direction ? "text-text-primary" : "text-text-tertiary")}
    />
  );
}

/**
 * Rows of like facts, compared down the columns: a balance history, orders, staff lists. Columns
 * are data (`columns`), so a page never writes table markup; numbers end-align in mono and never
 * truncate. It scrolls sideways inside its frame on a narrow screen rather than squeezing.
 */
export function Table<Row>({
  caption,
  hideCaption = false,
  columns,
  rows,
  rowKey,
  sort,
  onSortChange,
  loading = false,
  loadingRows = 5,
  empty,
  footer,
  density = "comfortable",
  className,
}: TableProps<Row>) {
  const captionId = useId();
  const headerKey = (columns.find((c) => c.rowHeader) ?? columns[0])?.key;
  const showEmpty = !loading && rows.length === 0 && empty !== undefined;
  const cellClass = (column: TableColumn<Row>) =>
    cn(
      cellStyles({
        density,
        end: column.numeric || column.align === "end",
        numeric: column.numeric,
        nowrap: column.nowrap,
      }),
      column.hideBelow && HIDE[column.hideBelow],
    );
  const nextSort = (key: string): TableSort => ({
    key,
    direction: sort?.key === key && sort.direction === "desc" ? "asc" : "desc",
  });

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border-subtle bg-bg-surface",
        className,
      )}
    >
      <section
        aria-labelledby={captionId}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling region takes focus so a keyboard can scroll it sideways.
        tabIndex={0}
        className={cn("overflow-x-auto", FOCUS_RING)}
      >
        <table
          aria-busy={loading || undefined}
          className="w-full border-collapse font-body text-body-b2 text-text-primary"
        >
          <caption
            id={captionId}
            className={cn(
              "px-4 pt-4 pb-1 text-start font-body text-heading-h3 text-text-primary",
              hideCaption && "sr-only",
            )}
          >
            {caption}
          </caption>
          <thead>
            <tr className="border-b border-border-subtle">
              {columns.map((column) => {
                const direction = sort?.key === column.key ? sort.direction : undefined;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      direction === "asc"
                        ? "ascending"
                        : direction === "desc"
                          ? "descending"
                          : undefined
                    }
                    style={column.width ? { width: column.width } : undefined}
                    className={cn(
                      cellClass(column),
                      "py-2.5 font-body text-body-b3-strong whitespace-nowrap text-text-secondary",
                    )}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(nextSort(column.key))}
                        className={cn(
                          "-mx-1 inline-flex cursor-pointer items-center gap-1 rounded-xs px-1 hover:text-text-primary",
                          (column.numeric || column.align === "end") && "flex-row-reverse",
                          FOCUS_RING,
                        )}
                      >
                        {column.header}
                        <SortIcon direction={direction} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} className="border-b border-border-subtle last:border-b-0">
                {columns.map((column) =>
                  column.key === headerKey ? (
                    <th
                      key={column.key}
                      scope="row"
                      className={cn(cellClass(column), "font-normal")}
                    >
                      {column.cell(row)}
                    </th>
                  ) : (
                    <td key={column.key} className={cellClass(column)}>
                      {column.cell(row)}
                    </td>
                  ),
                )}
              </tr>
            ))}
            {loading &&
              placeholderKeys(loadingRows).map((key) => (
                <tr key={key} aria-hidden className="border-b border-border-subtle last:border-b-0">
                  {columns.map((column) => (
                    <td key={column.key} className={cellClass(column)}>
                      <Skeleton
                        className={cn("h-4", column.numeric ? "ml-auto w-16" : "w-full max-w-40")}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            {showEmpty && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-text-secondary">
                  {empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      {footer && <div className="border-t border-border-subtle">{footer}</div>}
    </div>
  );
}
